import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Typography, Space, message, Table, Tag, Tooltip, Popconfirm } from 'antd';
import {
    FolderOutlined, FolderAddOutlined, DeleteOutlined, GroupOutlined, SettingOutlined,
} from '@ant-design/icons';
import { useKeycloak } from '@react-keycloak/web';
import { deleteDataCollection, getUsers } from '../../services/api';
import { formatFileSize, StatusDot, getDataCollectionsTablePagination } from './collectionUtils';
import { relativeTime, fullDateTime } from '../../utils/relativeTime';
import SegmentedToggle from '../SegmentedToggle';
import { useSearch } from '../../contexts/SearchContext';
import { useUserSession } from '../../contexts/UserSessionContext';
import { useCollectionsData } from '../../contexts/CollectionsDataContext';
import CollectionOwnerSelect from './CollectionOwnerSelect';

const { Text } = Typography;

const CollectionsList = ({ onOpenCollection, onCreateNew }) => {
    const navigate = useNavigate();
    const { keycloak } = useKeycloak();
    const { registerSearchHandler, query: headerSearchQuery } = useSearch();
    const { isAdmin, rolesLoaded, groupsMap } = useUserSession();
    const {
        userCollections,
        platformCollections,
        collectionsLoading,
        ensureCollections,
        invalidateCollections,
    } = useCollectionsData();

    const [activeTab, setActiveTab] = useState('personal');
    const [adminUsersMap, setAdminUsersMap] = useState({});
    const [adminUsersLoading, setAdminUsersLoading] = useState(false);

    const refreshCollections = useCallback(async () => {
        invalidateCollections();
        await ensureCollections(true);
    }, [invalidateCollections, ensureCollections]);

    useEffect(() => {
        if (rolesLoaded && keycloak?.authenticated) {
            ensureCollections();
        }
    }, [rolesLoaded, keycloak?.authenticated, ensureCollections]);

    useEffect(() => {
        if (!isAdmin || activeTab !== 'platform' || !keycloak?.authenticated) return;
        setAdminUsersLoading(true);
        getUsers(keycloak, null, 0, 2000)
            .then((list) => {
                const m = {};
                (list || []).forEach((u) => { m[u.id] = u.username || u.email || u.id; });
                setAdminUsersMap(m);
            })
            .catch(() => {})
            .finally(() => setAdminUsersLoading(false));
    }, [isAdmin, activeTab, keycloak]);

    const adminUserOptions = useMemo(() => Object.entries(adminUsersMap)
        .map(([value, label]) => ({ value, label }))
        .sort((a, b) => a.label.localeCompare(b.label)), [adminUsersMap]);

    const searchPool = useMemo(() => (
        isAdmin && platformCollections.length ? platformCollections : userCollections
    ), [isAdmin, platformCollections, userCollections]);

    useEffect(() => {
        return registerSearchHandler('Collections', (qRaw) => {
            const q = qRaw.trim().toLowerCase();
            if (!q) return [];
            return searchPool
                .filter(c => c.name?.toLowerCase().includes(q) || c.description?.toLowerCase().includes(q))
                .slice(0, 8)
                .map(c => ({ id: c.id, title: c.name, subtitle: c.description, type: 'collection', action: () => onOpenCollection(c) }));
        });
    }, [registerSearchHandler, searchPool, onOpenCollection]);

    const personalCount = userCollections.filter(c => !c.group_id).length;
    const groupCount = userCollections.filter(c => !!c.group_id).length;
    const platformCount = platformCollections.length;

    const filteredCollections = useMemo(() => {
        let result = activeTab === 'platform' && isAdmin ? platformCollections : userCollections;
        if (activeTab === 'personal') result = result.filter(c => !c.group_id);
        else if (activeTab === 'group') result = result.filter(c => !!c.group_id);
        if (headerSearchQuery.trim()) {
            const q = headerSearchQuery.toLowerCase();
            result = result.filter(c => c.name?.toLowerCase().includes(q) || c.description?.toLowerCase().includes(q));
        }
        return result;
    }, [userCollections, platformCollections, activeTab, isAdmin, headerSearchQuery]);

    const handleDeleteCollection = useCallback(async (collection) => {
        try {
            await deleteDataCollection(keycloak, collection.id);
            message.success('Collection deleted');
            await refreshCollections();
        } catch (err) { message.error(err.message || 'Failed to delete'); }
    }, [keycloak, refreshCollections]);

    const tabOptions = useMemo(() => {
        const opts = [
            { value: 'personal', text: 'Personal', count: personalCount },
            { value: 'group', text: 'Group', count: groupCount },
        ];
        if (isAdmin) opts.push({ value: 'platform', text: 'Platform', count: platformCount });
        return opts;
    }, [personalCount, groupCount, platformCount, isAdmin]);

    const collectionColumns = useMemo(() => {
        const cols = [
        {
            title: 'Name', dataIndex: 'name', key: 'name', ellipsis: true,
            render: (text, record) => (
                <div className="collection-name-cell">
                    <FolderOutlined style={{ fontSize: 18, color: 'var(--color-warning)', flexShrink: 0 }} />
                    <div className="collection-name-text">
                        <div className="collection-name-row">
                            <span className="collection-name-label">{text}</span>
                            <StatusDot status={record.embeddings_status} />
                            {record.group_id && (
                                <Tag color="purple" icon={<GroupOutlined />} style={{ marginLeft: 4, flexShrink: 0 }}>
                                    {groupsMap[record.group_id] || record.group_name || 'Group'}
                                </Tag>
                            )}
                        </div>
                        {record.description && (
                            <div className="collection-desc-label">{record.description}</div>
                        )}
                    </div>
                </div>
            ),
        },
        ];
        if (isAdmin) {
            cols.push({
                title: 'Owner',
                key: 'owner',
                width: 148,
                render: (_, record) => (
                    <div
                        className="collection-owner-cell"
                        onClick={e => e.stopPropagation()}
                        onMouseDown={e => e.stopPropagation()}
                    >
                        <CollectionOwnerSelect
                            ownerId={record.owner_id}
                            options={adminUserOptions}
                            loading={adminUsersLoading}
                            collectionId={record.id}
                            collectionName={record.name}
                            keycloak={keycloak}
                            onUpdated={refreshCollections}
                            className="collection-owner-inline-select"
                            getPopupContainer={() => document.body}
                        />
                    </div>
                ),
            });
        }
        cols.push(
        {
            title: 'Files', key: 'files', width: 90, align: 'center',
            render: (_, r) => (
                <div style={{ lineHeight: 1.3, textAlign: 'center' }}>
                    <div style={{ fontWeight: 600 }}>{r.total_files || r.file_count || 0}</div>
                    <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{formatFileSize(r.total_size || 0)}</div>
                </div>
            ),
        },
        {
            title: 'Chunks', key: 'rows', width: 80, align: 'center',
            render: (_, r) => { const c = r.total_rows || r.row_count || 0; return c > 0 ? c.toLocaleString() : '-'; },
        },
        {
            title: 'Model', key: 'model', width: 220, ellipsis: true,
            render: (_, r) => {
                const im = r.indexing_method;
                const type = im === 'pageindex' ? 'PageIndex' : im === 'hybrid' ? 'Hybrid' : 'Vector';
                const model = (im === 'pageindex' ? r.pageindex_model : r.embedding_model_name) || 'N/A';
                const color = im === 'pageindex' ? 'geekblue' : im === 'hybrid' ? 'green' : 'blue';
                return <Tag color={color} style={{ maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>{type} · {model}</Tag>;
            },
        },
        {
            title: 'Created', dataIndex: 'created_at', key: 'created_at', width: 120,
            render: (date) => date ? <Tooltip title={fullDateTime(date)}><span style={{ whiteSpace: 'nowrap' }}>{relativeTime(date)}</span></Tooltip> : '-',
        },
        {
            title: '',
            key: 'actions',
            width: 88,
            align: 'center',
            className: 'actions-cell',
            render: (_, record) => (
                <Space
                    size={4}
                    className="collections-list-actions-toolbar assistants-actions-toolbar"
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                >
                    <Tooltip title="Collection settings">
                        <Button
                            type="text"
                            size="small"
                            icon={<SettingOutlined />}
                            aria-label="Collection settings"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                navigate(`/collections/${record.public_id || record.id}/edit`);
                            }}
                        />
                    </Tooltip>
                    <Popconfirm
                        title="Delete collection"
                        description={`Delete "${record.name}" and all its files? This cannot be undone.`}
                        onConfirm={(e) => {
                            e?.stopPropagation();
                            handleDeleteCollection(record);
                        }}
                        okText="Delete"
                        okType="danger"
                        cancelText="Cancel"
                    >
                        <Tooltip title="Delete">
                            <Button
                                danger
                                type="text"
                                icon={<DeleteOutlined />}
                                size="small"
                                aria-label="Delete collection"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                }}
                            />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            ),
        },
        );
        return cols;
    }, [isAdmin, adminUserOptions, adminUsersLoading, groupsMap, handleDeleteCollection, keycloak, refreshCollections, navigate]);

    const getEmptyText = () => {
        if (activeTab === 'personal') return (
            <div className="empty-state">
                <FolderOutlined className="empty-state-icon" />
                <div className="empty-state-title">No personal collections</div>
                <Button type="primary" className="empty-state-action" onClick={onCreateNew}><FolderAddOutlined /> Create Your First Collection</Button>
            </div>
        );
        if (activeTab === 'group') return (
            <div className="empty-state">
                <GroupOutlined className="empty-state-icon" />
                <div className="empty-state-title">No group collections found</div>
                <div className="empty-state-description">Create a collection with "Team Access" visibility to share it with your group.</div>
                <Button type="primary" className="empty-state-action" onClick={onCreateNew}><FolderAddOutlined /> Create Group Collection</Button>
            </div>
        );
        return (<div className="empty-state"><FolderOutlined className="empty-state-icon" /><div className="empty-state-title">No collections found</div></div>);
    };

    return (
        <>
            <div className="collections-page-header">
                <div className="page-header" style={{ flex: 1, minWidth: 0 }}>
                    <h2 className="page-header-title">Data Collections</h2>
                    <span className="page-header-subtitle">
                        {activeTab === 'personal' && 'Your personal data collections'}
                        {activeTab === 'group' && 'Group-shared data collections'}
                        {activeTab === 'platform' && 'All platform data collections'}
                    </span>
                </div>
                <Button type="primary" icon={<FolderAddOutlined />} onClick={onCreateNew} className="new-collection-btn">
                    New Collection
                </Button>
            </div>

            <SegmentedToggle options={tabOptions} value={activeTab} onChange={setActiveTab} />

            <div className="collections-table-wrapper collections-table-animate collections-list-table-wrapper">
                <Table
                    columns={collectionColumns}
                    dataSource={filteredCollections}
                    rowKey="id"
                    loading={collectionsLoading && userCollections.length === 0 && platformCollections.length === 0}
                    pagination={getDataCollectionsTablePagination(10, { totalItemLabel: 'collections' })}
                    scroll={{ x: 'max-content' }}
                    locale={{ emptyText: getEmptyText() }}
                    onRow={(record) => ({
                        onClick: () => onOpenCollection(record),
                        style: { cursor: 'pointer' },
                    })}
                />
            </div>
        </>
    );
};

export default CollectionsList;
